import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaUserTie, FaUserFriends, FaEye, FaEyeSlash } from 'react-icons/fa';

const Login = () => {
  const [activeTab, setActiveTab] = useState('teacher');
  const [credentials, setCredentials] = useState({
    email: '',
    password: '',
    access_key: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    subject: '',
    school: ''
  });

  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRegisterInputChange = (e) => {
    const { name, value } = e.target;
    setRegisterData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(credentials, activeTab);
        if (result.success) {
          navigate('/dashboard');
        } else {
          setError(result.error);
          
          // Se for erro de email não verificado, mostrar opção de reenvio
          if (result.requiresVerification) {
            setError(`${result.error}\n\nSe você não recebeu o email de verificação, pode solicitar um novo abaixo.`);
          }
        }
      } else {
        // Verificar se é professor e cadastro está desabilitado
        if (activeTab === 'teacher') {
          setError('Cadastro de novos professores está temporariamente desabilitado. Entre em contato com o administrador.');
          setLoading(false);
          return;
        }

        // Validar confirmação de senha
        if (registerData.password !== registerData.confirmPassword) {
          setError('As senhas não coincidem');
          setLoading(false);
          return;
        }

        // Remover confirmPassword antes de enviar
        const { confirmPassword, ...dataToSend } = registerData;
        const result = await register(dataToSend);
        if (result.success) {
          if (result.requiresVerification) {
            setError('');
            setSuccess('Cadastro realizado! Verifique seu email para ativar a conta.');
            // Voltar para o modo login
            setIsLogin(true);
          } else {
          navigate('/dashboard');
          }
        } else {
          setError(result.error);
        }
      }
    } catch (err) {
      setError('Erro inesperado. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCredentials({
      email: '',
      password: '',
      access_key: ''
    });
    setRegisterData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      subject: '',
      school: ''
    });
    setError('');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setIsLogin(true);
    resetForm();
  };

  const tabs = [
    { id: 'teacher', label: 'Professor', icon: FaUserTie },
    { id: 'student', label: 'Aluno', icon: FaUser },
    { id: 'guardian', label: 'Responsável', icon: FaUserFriends }
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            hmwkStudio
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Faça login para acessar sua conta
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-6">
          {/* Tabs */}
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex-1 flex items-center justify-center space-x-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Professor - Toggle Login/Register */}
          {activeTab === 'teacher' && (
            <div className="mb-4">
              <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => setIsLogin(true)}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    isLogin
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Entrar
                </button>
                <button
                  disabled
                  className="flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors bg-gray-200 text-gray-400 cursor-not-allowed"
                  title="Cadastro de novos professores está temporariamente desabilitado"
                >
                  Cadastrar
                </button>
              </div>
              {!isLogin && (
                <div className="mt-2 p-3 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded text-sm">
                  ⚠️ Cadastro de novos professores está temporariamente desabilitado. Entre em contato com o administrador.
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded whitespace-pre-line">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Professor Login */}
            {activeTab === 'teacher' && isLogin && (
              <>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    E-mail
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={credentials.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="seu-email@exemplo.com"
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    Senha
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={credentials.password}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10"
                      placeholder="Sua senha"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Professor Register */}
            {activeTab === 'teacher' && !isLogin && (
              <>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Completo *
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={registerData.name}
                    onChange={handleRegisterInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Seu nome completo"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    E-mail *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={registerData.email}
                    onChange={handleRegisterInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="seu-email@exemplo.com"
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    Senha *
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      minLength="8"
                      value={registerData.password}
                      onChange={handleRegisterInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10"
                      placeholder="Mínimo 8 caracteres"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  <small className="text-gray-500">Mínimo 8 caracteres, com maiúscula, minúscula e número</small>
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirmar Senha *
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength="8"
                    value={registerData.confirmPassword}
                    onChange={handleRegisterInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      registerData.confirmPassword && registerData.password !== registerData.confirmPassword 
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-gray-300'
                    }`}
                    placeholder="Digite a senha novamente"
                  />
                  {registerData.confirmPassword && registerData.password !== registerData.confirmPassword && (
                    <small className="text-red-500">As senhas não coincidem</small>
                  )}
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Matéria *
                  </label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={registerData.subject}
                    onChange={handleRegisterInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ex: Matemática, Português, História..."
                  />
                </div>
                <div>
                  <label htmlFor="school" className="block text-sm font-medium text-gray-700 mb-1">
                    Escola <span className="text-gray-400">(opcional)</span>
                  </label>
                  <input
                    id="school"
                    name="school"
                    type="text"
                    value={registerData.school}
                    onChange={handleRegisterInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nome da escola (opcional)"
                  />
                </div>
              </>
            )}

            {/* Student/Guardian Login */}
            {(activeTab === 'student' || activeTab === 'guardian') && (
              <div>
                <label htmlFor="access_key" className="block text-sm font-medium text-gray-700 mb-1">
                  Chave de Acesso
                </label>
                <input
                  id="access_key"
                  name="access_key"
                  type="text"
                  required
                  value={credentials.access_key}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Digite sua chave de acesso"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Chave fornecida pelo professor (formato: XXXXX-XXX)
                </p>
              </div>
            )}

            {/* Link Esqueci minha senha - apenas para professores */}
            {activeTab === 'teacher' && isLogin && (
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Esqueci minha senha
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Carregando...' : (isLogin ? 'Entrar' : 'Cadastrar')}
            </button>

            {/* Reenviar verificação - apenas se houver erro de verificação */}
            {error && error.includes('Email não verificado') && isLogin && activeTab === 'teacher' && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <h4 className="text-sm font-medium text-blue-800 mb-2">
                  Precisa de ajuda com a verificação?
                </h4>
                <button
                  type="button"
                  onClick={() => navigate('/verify-email')}
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline mr-4"
                >
                  Reenviar email de verificação
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login; 